import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { T, PageHeader, Section, Field, Row, Alert, Badge } from '../components/UI';
import { useAuth } from '../context/AuthContext';

const STATUS_MAP = {
  submitted:    { label:'Submitted',    bg:'var(--blue-light)',  color:'var(--blue)'  },
  under_review: { label:'Under Review', bg:'var(--amber-bg)',    color:'var(--amber)' },
  resolved:     { label:'Resolved',     bg:'var(--green-bg)',    color:'var(--green)' },
  closed:       { label:'Closed',       bg:'var(--bg)',          color:'var(--muted)' },
};

export default function IncidentDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner  = user?.role === 'owner';

  const [report,      setReport]      = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [reviewing,   setReviewing]   = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [saving,      setSaving]      = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    api.get(`/incidents/${id}`)
      .then(({data})=>{ setReport(data.report); setReviewNotes(data.report.reviewNotes||''); })
      .catch(()=>setError('Failed to load report'))
      .finally(()=>setLoading(false));
  }, [id]);

  const handleReview = async (status) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/incidents/${id}`, { status, reviewNotes });
      setReport(data.report);
      setReviewing(false);
    } catch (err) { setError(err.response?.data?.message||'Failed to update'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to permanently delete this incident report?\n\nAll associated photos, signatures and data will be removed. This cannot be undone.`)) return;
    if (!await confirmDelete('All photos, signatures and associated data will be permanently removed.')) return;
    try { await api.delete(`/incidents/${id}`); navigate('/incidents'); }
    catch { setError('Failed to delete'); }
  };



  if (loading) return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Inter,sans-serif', color:'var(--muted)' }}>Loading...</div>;
  if (error || !report) return <div style={{ padding:'40px', color:'var(--red)', fontFamily:'Inter,sans-serif' }}>{error||'Report not found'}</div>;

  const st = STATUS_MAP[report.status] || STATUS_MAP.submitted;

  return (
    <div style={T.page}>
      <PageHeader
        title="Incident Report"
        onBack={()=>navigate('/incidents')}
        actions={
          <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
            <Badge label={st.label} color={st.color} bg={st.bg}/>
            {isOwner && !reviewing && (
              <button onClick={()=>setReviewing(true)} style={{ ...T.btnPrimary, fontSize:'13px' }}>
                <i className="ti ti-clipboard-check" aria-hidden="true"/> Review
              </button>
            )}
            {isOwner && (
              <button onClick={handleDelete} style={{ ...T.btnIcon, color:'var(--red)', borderColor:'rgba(163,45,45,0.25)' }}>
                <i className="ti ti-trash" aria-label="Delete"/>
              </button>
            )}
          </div>
        }
      />

      <div style={{ maxWidth:'680px', margin:'0 auto', padding:'20px 16px 80px' }}>

        {/* Review panel */}
        {reviewing && (
          <div style={{ background:'var(--blue-light)', border:'1px solid var(--blue)', borderRadius:'var(--radius-lg)', padding:'16px', marginBottom:'16px' }}>
            <div style={{ fontSize:'14px', fontWeight:'500', color:'var(--blue)', marginBottom:'12px' }}>Review incident report</div>
            <Field label="Review notes">
              <textarea value={reviewNotes} onChange={e=>setReviewNotes(e.target.value)}
                rows={3} style={{ ...T.input, resize:'vertical' }} placeholder="Add review notes or actions taken..."/>
            </Field>
            <div style={{ display:'flex', gap:'8px', marginTop:'12px', flexWrap:'wrap' }}>
              <button onClick={()=>handleReview('under_review')} disabled={saving} style={{ ...T.btnSecondary, flex:1 }}>Mark Under Review</button>
              <button onClick={()=>handleReview('resolved')} disabled={saving} style={{ ...T.btnPrimary, flex:1, background:'var(--green)' }}>Mark Resolved</button>
              <button onClick={()=>handleReview('closed')} disabled={saving} style={{ ...T.btnSecondary, flex:1 }}>Close</button>
            </div>
            <button onClick={()=>setReviewing(false)} style={{ marginTop:'8px', background:'none', border:'none', color:'var(--muted)', fontSize:'13px', cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
          </div>
        )}

        {report.reviewNotes && !reviewing && (
          <div style={{ background:'var(--green-bg)', border:'1px solid var(--green)', borderRadius:'var(--radius-lg)', padding:'12px', marginBottom:'16px' }}>
            <div style={{ fontSize:'12px', fontWeight:'500', color:'var(--green)', marginBottom:'4px' }}>Owner review notes</div>
            <div style={{ fontSize:'13px', color:'var(--text)' }}>{report.reviewNotes}</div>
          </div>
        )}

        {/* Incident details */}
        <Section title="Incident Details" accent>
          <Row cols={2}>
            <Field label="Date"><div style={T.readField}>{new Date(report.date).toLocaleDateString('en-AU',{day:'2-digit',month:'long',year:'numeric'})}</div></Field>
            <Field label="Time"><div style={T.readField}>{report.time}</div></Field>
          </Row>
          <Field label="Type">
            <div style={T.readField}>{report.locationType==='transit' ? `In transit: ${report.transitFrom} → ${report.transitTo}` : `At location: ${report.location}`}</div>
          </Field>
          <Field label="Description"><div style={{ ...T.readField, whiteSpace:'pre-wrap' }}>{report.description}</div></Field>
        </Section>

        {/* Vehicle */}
        {(report.make||report.model||report.stockNum||report.vinNum) && (
          <Section title="Vehicle" accent>
            <Row cols={2}>
              {report.make && <Field label="Make"><div style={T.readField}>{report.make}</div></Field>}
              {report.model && <Field label="Model"><div style={T.readField}>{report.model}</div></Field>}
              {report.stockNum && <Field label="Stock #"><div style={{ ...T.readField, fontFamily:'JetBrains Mono,monospace', color:'var(--blue)' }}>{report.stockNum}</div></Field>}
              {report.vinNum && <Field label="VIN"><div style={{ ...T.readField, fontFamily:'JetBrains Mono,monospace' }}>{report.vinNum}</div></Field>}
            </Row>
          </Section>
        )}

        {/* Injuries */}
        <Section title="Injuries" accent>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'10px 12px', background:report.hasInjuries?'var(--red-bg)':'var(--green-bg)', borderRadius:'var(--radius)', border:`1px solid ${report.hasInjuries?'var(--red)':'var(--green)'}` }}>
            <i className={`ti ${report.hasInjuries?'ti-alert-circle':'ti-check'}`} style={{ color:report.hasInjuries?'var(--red)':'var(--green)', fontSize:'16px' }} aria-hidden="true"/>
            <span style={{ fontSize:'13px', fontWeight:'500', color:report.hasInjuries?'var(--red)':'var(--green)' }}>{report.hasInjuries?'Injuries occurred':'No injuries reported'}</span>
          </div>
          {report.hasInjuries && report.injuryDetails && (
            <Field label="Injury details" style={{ marginTop:'12px' }}><div style={{ ...T.readField, whiteSpace:'pre-wrap' }}>{report.injuryDetails}</div></Field>
          )}
        </Section>

        {/* People */}
        <Section title="People Involved" accent>
          <Row cols={2}>
            <Field label="Reported by"><div style={T.readField}>{report.reportedBy||'—'}</div></Field>
            <Field label="Who did the damage"><div style={T.readField}>{report.personInvolved||'—'}</div></Field>
          </Row>
          {report.witnesses?.length > 0 && (
            <Field label="Witnesses">
              {report.witnesses.map((w,i)=>(
                <div key={i} style={{ ...T.readField, marginBottom:'6px' }}>
                  <span style={{ fontWeight:'500' }}>{w.name}</span>
                  {w.phone && <span style={{ color:'var(--muted)' }}> · {w.phone}</span>}
                  {w.email && <span style={{ color:'var(--muted)' }}> · {w.email}</span>}
                </div>
              ))}
            </Field>
          )}
          {report.thirdParty?.involved && (
            <div style={{ background:'var(--amber-bg)', border:'1px solid var(--amber)', borderRadius:'var(--radius)', padding:'12px', marginTop:'8px' }}>
              <div style={{ fontSize:'12px', fontWeight:'500', color:'var(--amber)', marginBottom:'8px' }}>Third party involved</div>
              <Row cols={2}>
                <Field label="Name"><div style={T.readField}>{report.thirdParty.name||'—'}</div></Field>
                <Field label="Contact"><div style={T.readField}>{report.thirdParty.contact||'—'}</div></Field>
              </Row>
              {report.thirdParty.insurance && <Field label="Insurance"><div style={T.readField}>{report.thirdParty.insurance}</div></Field>}
            </div>
          )}
        </Section>

        {/* Photos */}
        {report.photos?.length > 0 && (
          <Section title="Photos" accent>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'10px' }}>
              {report.photos.map((p,i)=>(
                <img key={i} src={p.url} alt={p.name||`Photo ${i+1}`}
                  onClick={()=>setSelectedPhoto(p.url)}
                  style={{ width:'100px', height:'100px', objectFit:'cover', borderRadius:'var(--radius)', border:'1px solid var(--border)', cursor:'pointer' }}/>
              ))}
            </div>
          </Section>
        )}

        {/* Licence */}
        <Section title="Driver Licence" accent>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'10px 12px', background:report.licenseSubmitted?'var(--green-bg)':'var(--red-bg)', borderRadius:'var(--radius)', border:`1px solid ${report.licenseSubmitted?'var(--green)':'var(--red)'}`, marginBottom: report.licensePhotoData?'12px':'0' }}>
            <i className={`ti ${report.licenseSubmitted?'ti-check':'ti-x'}`} style={{ color:report.licenseSubmitted?'var(--green)':'var(--red)', fontSize:'16px' }} aria-hidden="true"/>
            <span style={{ fontSize:'13px', fontWeight:'500', color:report.licenseSubmitted?'var(--green)':'var(--red)' }}>{report.licenseSubmitted?'Driver licence submitted':'Driver licence NOT submitted'}</span>
          </div>
          {report.licensePhotoData && (
            <div>
              <div style={{ fontSize:'12px', color:'var(--muted)', marginBottom:'6px' }}>Licence photo</div>
              <img src={report.licensePhotoData} alt="Driver licence"
                onClick={()=>setSelectedPhoto(report.licensePhotoData)}
                style={{ width:'100%', maxHeight:'200px', objectFit:'cover', borderRadius:'var(--radius)', border:'1px solid var(--border)', cursor:'pointer' }}/>
            </div>
          )}
        </Section>

        {/* Signatures */}
        {(report.signatureAuthorised || report.signaturePerson) && (
          <Section title="Digital Signatures" accent>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>
              {report.signatureAuthorised && (
                <div>
                  <div style={{ fontSize:'12px', color:'var(--muted)', marginBottom:'6px' }}>Car Saloon authorised signatory</div>
                  <img src={report.signatureAuthorised} alt="Authorised signature" style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'#fafafa' }}/>
                </div>
              )}
              {report.signaturePerson && (
                <div>
                  <div style={{ fontSize:'12px', color:'var(--muted)', marginBottom:'6px' }}>Person involved</div>
                  <img src={report.signaturePerson} alt="Person signature" style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'#fafafa' }}/>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Meta */}
        <div style={{ fontSize:'12px', color:'var(--hint)', textAlign:'center', marginTop:'16px' }}>
          Submitted by {report.submittedByName} · {new Date(report.createdAt).toLocaleDateString('en-AU',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}
          {report.reviewedBy && ` · Reviewed by ${report.reviewedBy?.name || report.reviewedBy}`}
        </div>
      </div>

      {/* Photo lightbox */}
      {selectedPhoto && (
        <div onClick={()=>setSelectedPhoto(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
          <img src={selectedPhoto} alt="Incident photo" style={{ maxWidth:'100%', maxHeight:'90vh', borderRadius:'var(--radius)', objectFit:'contain' }}/>
          <button onClick={()=>setSelectedPhoto(null)} style={{ position:'absolute', top:'16px', right:'16px', background:'rgba(255,255,255,0.15)', border:'none', borderRadius:'50%', width:'36px', height:'36px', cursor:'pointer', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <i className="ti ti-x" style={{ fontSize:'18px' }} aria-label="Close"/>
          </button>
        </div>
      )}
    </div>
  );
}
